import { ActionWithPayload, RehydrateOptions } from "@obg/ngrx-utils";
import { uniq } from "lodash-es";

import { SESSION_ACTION_TYPE } from "../auth/session.action";
import { LoginRequest } from "../auth/session.model";
import { Jurisdiction } from "../jurisdiction/jurisdiction.model";

import { CUSTOMER_MANAGEMENT_ACTION_TYPE } from "./customer-management.action";
import { CredentialType, Customer, CustomerCredentialType, CustomerManagementState } from "./customer-management.model";

const INITIAL_STATE: CustomerManagementState = {
	isDepositTermsAndConditionsActive: false,
	isProfileUpdateSuccess: false,
	persistentData: {},
};

export function customerManagementReducer(state = INITIAL_STATE, action: ActionWithPayload): CustomerManagementState {
	function updateState(newState: any) {
		return Object.assign({}, state, newState);
	}

	// todo: Add explicit actions SET_CREDENTIALS, CLEAR_CREDENTIALS and add effects to map them to auth actions
	// also clear credentials on Longin fail for other reasons thank t&c not accepted
	switch (action.type) {
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerSuccess:
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerSuccess: {
			return updateState({
				customer: {
					...action.payload,
					allowedActions: {
						...state.customer?.allowedActions,
						...action.payload.allowedActions,
					},
				},
				hasAcceptedLatestTermsAndConditions: undefined,
				hasDeclinedLatestTermsAndConditions: undefined,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.clearCustomer: {
			const preserveExpiredSessionUsername = action.payload as boolean;

			return updateState({
				customer: undefined,
				hasAcceptedLatestTermsAndConditions: undefined,
				hasDeclinedLatestTermsAndConditions: undefined,
				hasDismissedLatestTermsAndConditions: undefined,
				isProfileUpdateSuccess: undefined,
				expiredSessionUsername:
					preserveExpiredSessionUsername && state.customer
						? state.customer.loginInformation.username
						: undefined,
				persistentData: {},
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerDetailSuccess:
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerDetailSuccess: {
			const customerDetails: Partial<Customer> = action.payload;
			return updateState({
				customer: Object.assign({}, state.customer, customerDetails),
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerSubscriptions: {
			return updateState({
				customer: Object.assign({}, state.customer, action.payload),
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.checkEmailAvailabilitySuccess: {
			return updateState({
				emailAvailability: action.payload,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.latestTermsAndConditionsNotAccepted: {
			const jurisdiction = action.payload.data?.termsAndConditionsNotAcceptedLoginResponse
				?.jurisdiction as Jurisdiction;
			return updateState({
				hasAcceptedLatestTermsAndConditions: false,
				hasDeclinedLatestTermsAndConditions: undefined,
				termsAndConditionsJurisdiction: jurisdiction,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptTermsAndConditionsSuccess: {
			return updateState({
				hasAcceptedLatestTermsAndConditions: true,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.clearAcceptedLatestTermsAndConditions: {
			return updateState({
				hasAcceptedLatestTermsAndConditions: undefined,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.declineTermsAndConditionsSuccess: {
			return updateState({
				hasDeclinedLatestTermsAndConditions: true,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.dismissAcceptTermsAndConditions: {
			return updateState({
				hasDismissedLatestTermsAndConditions: true,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.clearDismissedTermsAndConditions: {
			return updateState({
				hasDismissedLatestTermsAndConditions: false,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.confirmProfileSuccess: {
			return updateState({
				isProfileUpdateSuccess: true,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptEntityLegalNoticeSuccess: {
			return updateState({
				hasAcceptedEntityLegalNotice: true,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.latestPrivacyPolicyNotAccepted: {
			return updateState({
				hasAcceptedLatestPrivacyPolicy: false,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptPrivacyPolicySuccess: {
			return updateState({
				hasAcceptedLatestPrivacyPolicy: true,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.clearAcceptedLatestPrivacyPolicy: {
			return updateState({
				hasAcceptedLatestPrivacyPolicy: undefined,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.activateDepositTermsAndConditions: {
			return updateState({
				isDepositTermsAndConditionsActive: true,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.cancelDepositTermsAndConditions: {
			return updateState({
				isDepositTermsAndConditionsActive: false,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptDepositTermsAndConditionsSuccess: {
			return updateState({
				isBusy: false,
				isDepositTermsAndConditionsActive: false,
				customer: Object.assign({}, state.customer, action.payload as Partial<Customer>),
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.unsubscribeFromNewsletterSuccess: {
			return updateState({
				hasUnsubscribedFromNewsletter: true,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCredentials:
		case SESSION_ACTION_TYPE.login:
		case SESSION_ACTION_TYPE.loginWithPersonalNumber: {
			return updateState({
				credentials: action.payload as LoginRequest,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.deleteCredentials: {
			return updateState({
				credentials: undefined,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerCredentialTypes: {
			const credentialType: CredentialType = {
				type: action.payload as CustomerCredentialType,
			};
			return updateState({
				customer: {
					...state.customer,
					credentialTypes: uniq([...(state.customer?.credentialTypes || []), credentialType]),
				},
			});
		}
		case SESSION_ACTION_TYPE.loginSuccess:
		case SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess:
		case SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess: {
			return updateState({
				credentials: undefined,
			});
		}
		case CUSTOMER_MANAGEMENT_ACTION_TYPE.updatePersistentData: {
			const payload = action.payload as Record<string, any>;
			return updateState({
				persistentData: { ...state.persistentData, ...payload },
			});
		}
		default:
			return state;
	}
}

customerManagementReducer.prototype.rehydrateOptions = (): RehydrateOptions<CustomerManagementState> => ({
	name: "customerManagementReducer",
	excludeProps: [
		"customer",
		"emailAvailability",
		"hasAcceptedLatestTermsAndConditions",
		"hasDeclinedLatestTermsAndConditions",
		"hasUnsubscribedFromNewsletter",
		"isDepositTermsAndConditionsActive",
		"credentials",
		"expiredSessionUsername",
		"isProfileUpdateSuccess",
	],
});
